import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import Logo from "../../components/shared/Logo";

const features = [
  { text: "SCORM-compliant course delivery", icon: Check },
  { text: "Instructor-led training sessions", icon: Check },
  { text: "Progress tracking & certificates", icon: Check },
  { text: "Multi-tenant enterprise deployment", icon: Check },
  { text: "Compliance & audit reporting", icon: Check },
];

const SignupPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error("Password too short", { description: "Password must be at least 8 characters" });
      return;
    }
    
    setLoading(true);
    
    // Store registration data temporarily and redirect to role selection
    sessionStorage.setItem("signup_data", JSON.stringify({ fullName, email, password }));
    toast.success("Great! Let's set up your account");
    navigate("/auth/role");
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen hero-gradient flex relative">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl floating" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl floating-delayed" />
      </div>

      {/* Left Side - Features */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12 relative z-10">
        <div className="max-w-md">
          <h2 className="text-4xl font-bold text-foreground mb-4" style={{ fontFamily: "'Sora', sans-serif" }}>
            Join as a <span className="gradient-text">Learner</span> or <span className="gradient-text">Enterprise</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Whether you're here to learn new skills or deploy training at scale, Odesie provides the tools you need.
          </p>

          <div className="space-y-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-3 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="text-foreground">{feature.text}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 flex gap-4">
            <div className="flex-1 p-4 rounded-xl bg-primary/10 border border-primary/20">
              <h4 className="font-semibold text-foreground mb-1">For Learners</h4>
              <p className="text-sm text-muted-foreground">Access courses, track progress, and earn certificates</p>
            </div>
            <div className="flex-1 p-4 rounded-xl bg-accent/10 border border-accent/20">
              <h4 className="font-semibold text-foreground mb-1">For Enterprises</h4>
              <p className="text-sm text-muted-foreground">Deploy branded LMS portals for your workforce</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 lg:p-12 relative z-10">
        <div className="w-full max-w-md animate-fade-in">
          {/* Back Button */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="glass-card p-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <Logo size="lg" />
            </div>

            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
                Create your account
              </h1>
              <p className="text-muted-foreground">Get started with Odesie</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name">Full name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="pl-10 h-12 bg-background/50 border-border/50 focus:border-primary"
                    required
                    data-testid="signup-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 bg-background/50 border-border/50 focus:border-primary"
                    required
                    data-testid="signup-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-12 bg-background/50 border-border/50 focus:border-primary"
                    required
                    data-testid="signup-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">Must be at least 8 characters</p>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-semibold btn-primary"
                disabled={loading}
                data-testid="signup-submit-btn"
              >
                {loading ? "Creating account..." : "Create account"}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                By signing up, you agree to our{" "}
                <Link to="/terms" className="text-primary hover:underline">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </Link>
              </p>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border/50" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-card text-muted-foreground">or sign up with</span>
              </div>
            </div>

            {/* Social Signup */}
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="h-12 bg-background/50" disabled data-testid="signup-google-btn">
                <img src="/logo-google.webp" alt="Google" className="w-5 h-5 mr-2 object-contain" />
                Google
              </Button>
              <Button variant="outline" className="h-12 bg-background/50" disabled data-testid="signup-microsoft-btn">
                <img src="/logo-microsoft.png" alt="Microsoft" className="w-5 h-5 mr-2 object-contain" />
                Microsoft
              </Button>
            </div>

            {/* Login Link */}
            <p className="text-center mt-6 text-muted-foreground">
              Already have an account?{" "}
              <Link to="/auth/login" className="text-primary font-medium hover:text-primary/80 transition-colors">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
